import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../../config/axios';
import ertinuLogo from '../../assets/images/ertinu_logo.png';
import aproLogo from '../../assets/images/apro_logo.png';
import forgotPasswordImage from '../../assets/images/forgot-password.png';

const LoadingDots = () => {
  return (
    <div className="flex space-x-1">
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_infinite]" />
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_0.3s_infinite]" />
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_0.6s_infinite]" />
    </div>
  );
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Domain-based logo switching
  const getLogoByDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('ertinu.com')) {
      return ertinuLogo;
    } else {
      return aproLogo; // localhost, apro.com.tr ve diğer tüm durumlar için
    }
  };

  // Token doğrulaması
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setError('Invalid password reset link. Please request a new link.');
        setValidatingToken(false);
        return;
      }

      try {
        // Token'ın geçerliliğini kontrol et
        await api.post('/auth/validate-reset-token', { 
          token, 
          email 
        });
        
        setTokenValid(true);
      } catch (err) {
        console.error('Token validation error:', err);
        if (err.response?.status === 400) {
          setError('The password reset link has expired or is invalid. Please request a new link.');
        } else {
          setError('An error occurred while verifying the connection. Please try again.');
        }
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, email]);

  const validatePassword = (pwd) => {
    // Şifre en az 8 karakter olmalı ve en az bir büyük harf, bir küçük harf ve bir rakam içermeli
    const minLength = pwd.length >= 8;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    
    return {
      valid: minLength && hasUpperCase && hasLowerCase && hasNumber,
      message: !minLength 
        ? 'Password must be at least 8 characters long.'
        : !hasUpperCase
        ? 'Password must contain at least one uppercase letter.'
        : !hasLowerCase
        ? 'Password must contain at least one lowercase letter.'
        : !hasNumber
        ? 'Password must contain at least one number.'
        : ''
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Şifre doğrulama
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        setError(passwordValidation.message);
        setLoading(false);
        return;
      }

      // Şifre eşleşme kontrolü
      if (password !== confirmPassword) {
        setError('Passwords do not match. Please check.');
        setLoading(false);
        return;
      }

      const response = await api.post('/auth/reset-password', { 
        token,
        email,
        password
      });

      // Yapay gecikme (0.5 saniye)
      await new Promise(resolve => setTimeout(resolve, 500));

      setSuccess('Your password has been updated successfully! You can log in with your new password.');
      
      // 3 saniye sonra login sayfasına yönlendir
      setTimeout(() => {
        // Email'e göre doğru login sayfasına yönlendir
        const isCustomerEmail = response.data?.user_type === 'customer';
        navigate(isCustomerEmail ? '/login' : '/admin');
      }, 3000);
      
    } catch (err) {
      console.error('Reset password error:', err);
      
      if (err.response?.status === 400) {
        setError('The password reset link has expired or is invalid. Please request a new link.');
      } else {
        setError(err.response?.data?.message || 'An error occurred while updating the password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Token doğrulanıyor ekranı
  if (validatingToken) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#272c39]">
        <div className="text-center">
          <div className="mb-4">
            <LoadingDots />
          </div>
          <p className="text-white/60">Connecting...</p>
        </div>
      </div>
    );
  }

  // Token geçersiz ise
  if (!tokenValid) {
    return (
      <div className="flex flex-col lg:flex-row h-screen">
        <div className="w-full lg:w-[500px] bg-[#272c39] h-full flex flex-col px-6 sm:px-8 lg:px-12 justify-center">
          <div className="text-center">
            <img src={getLogoByDomain()} alt="Company Logo" className="h-12 mx-auto mb-8" />
            <h2 className="text-2xl font-medium text-white mb-4">Invalid Request</h2>
            <div className="bg-red-500/10 text-red-400 text-sm p-4 rounded-md mb-6">
              {error}
            </div>
            <Link 
              to="/forgot-password"
              className="inline-block bg-white text-[#272c39] px-6 py-2 rounded-md font-medium hover:bg-white/90 transition-colors"
            >
              Request a New Reset Password Link
            </Link>
          </div>
        </div>

        <div className="hidden lg:flex flex-1 bg-[#f5f7f9] flex-col items-center justify-center">
          <div className="max-w-[600px] text-center">
            <img 
              src={forgotPasswordImage} 
              alt="Invalid Reset Link" 
              className="mx-auto max-h-[400px] object-contain mb-8"
            />
            <div className="text-center px-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Link Expired
              </h2>
              <p className="text-lg text-gray-600">
                Your password reset link has expired or is invalid. Don't worry, 
                you can request a new one and we'll send it to your email address.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen">
      <div className="w-full lg:w-[500px] bg-[#272c39] h-full flex flex-col px-6 sm:px-8 lg:px-12">
        <div className="mt-8 sm:mt-12 lg:mt-20 mb-8 sm:mb-12 lg:mb-16 flex justify-start w-full">
          <img src={getLogoByDomain()} alt="Company Logo" className="h-10 sm:h-12" />
        </div>

        <div className="w-full">
          <h2 className="text-xl sm:text-2xl font-medium text-white mb-2">Create New Password</h2>
          <p className="text-white/60 text-sm mb-6 sm:mb-8">
            Create a new password for your account
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 bg-white/10 border border-white/10 text-white rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent
                  placeholder-white/30 text-base"
                placeholder="At least 8 characters"
                required
                disabled={loading}
              />
              <p className="text-white/40 text-xs mt-1">
                At least 8 characters, one uppercase letter, one lowercase letter and one number
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 bg-white/10 border border-white/10 text-white rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent
                  placeholder-white/30 text-base"
                placeholder="Confirm your password"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !password.trim() || !confirmPassword.trim()}
              className={`w-full py-2.5 sm:py-2 rounded-md transition-all font-medium flex items-center justify-center
                disabled:cursor-not-allowed text-base ${
                loading || !password.trim() || !confirmPassword.trim()
                  ? 'bg-white/20 text-white/50' 
                  : 'bg-white text-[#272c39] hover:bg-white/90'
              }`}
            >
              {loading ? (
                <LoadingDots />
              ) : (
                'Update Password'
              )}
            </button>

            <div className="flex justify-center">
              <Link 
                to="/forgot-password"
                className="text-sm text-white/50 hover:text-white/90 transition-colors"
              >
                Reset Password Page
              </Link>
            </div>

            {error && (
              <div className="bg-red-500/10 text-red-400 text-sm p-3 rounded-md mt-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-500/10 text-green-400 text-sm p-3 rounded-md mt-4">
                {success}
                <div className="text-xs mt-2 text-green-300">
                  3 seconds after you will be redirected to the login page...
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="mt-auto mb-8 text-center w-full">
          <p className="text-white/40 text-sm">
            {window.location.hostname.includes('ertinu.com') 
              ? '© 2024 ERTINU. All rights reserved.'
              : '© 2024 APRO. All rights reserved.'
            }
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[#f5f7f9] flex-col items-center justify-center">
        <div className="max-w-[600px] text-center">
          <img 
            src={forgotPasswordImage} 
            alt="Reset Password" 
            className="mx-auto max-h-[400px] object-contain mb-8"
          />
          <div className="text-center px-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Create a Secure Password
            </h2>
            <p className="text-lg text-gray-600">
              Choose a strong password for your account. Your password must be at least 8 characters long 
              and contain one uppercase letter, one lowercase letter and one number.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
